import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Shield, HeartHandshake } from "lucide-react";
import FadeIn from "@/components/FadeIn";
import { IMAGES } from "@/lib/images";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRESETS = [
  { id: "tier_200", label: "$200", helper: "A day on the land" },
  { id: "tier_500", label: "$500", helper: "A weekend retreat" },
  { id: "tier_1000", label: "$1,000", helper: "A full week of quiet" },
];

// Clover-hosted checkout URLs — each tier has its own preset Clover payment link.
// "custom" is Clover's variable-amount link where the donor types their own amount.
const CLOVER_URLS = {
  tier_200: "https://link.clover.com/urlshortener/kbRdCH",
  tier_500: "https://link.clover.com/urlshortener/pdd64m",
  tier_1000: "https://link.clover.com/urlshortener/qhWdNJ",
  custom:   "https://link.clover.com/urlshortener/jSvFRq",
};

export default function Donate() {
  const [tier, setTier] = useState("tier_200");
  const [customAmount, setCustomAmount] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [dedication, setDedication] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("cancelled") === "1") {
      toast("Donation cancelled. The page is here whenever you're ready.", { icon: "✕" });
    }
  }, []);

  const onDonate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // 1) Record the donor intent server-side (notifies owner + sends donor an acknowledgment email)
      await axios.post(`${API}/donations/clover-intent`, {
        tier_id: tier,
        custom_amount: tier === "custom" && customAmount ? parseFloat(customAmount) : null,
        donor_name: name || null,
        donor_email: email || null,
        dedication: dedication || null,
      });
      // 2) Hand off to Clover's hosted checkout
      const cloverUrl = CLOVER_URLS[tier];
      if (!cloverUrl) {
        toast.error("Unable to open checkout. Please try again or contact us.");
        return;
      }
      window.location.href = cloverUrl;
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="donate-page">
      {/* HERO */}
      <section className="relative -mt-20 pt-32 lg:pt-44 pb-20 lg:pb-28 bg-ink text-cream overflow-hidden" data-testid="donate-hero">
        <div className="absolute inset-0 opacity-30">
          <img src={IMAGES.heroHome} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-ink via-ink/75 to-ink" />
        </div>
        <div className="relative container-x">
          <FadeIn><p className="overline text-cream/50 mb-6">Donate · The Shadow Foundation 501(c)(3)</p></FadeIn>
          <FadeIn delay={120}>
            <h1 className="font-serif text-5xl lg:text-[80px] font-light leading-[1.05] tracking-tight max-w-4xl">
              Underwrite a day of <em className="italic text-cream/70">quiet rest</em> for
              someone who carries weight.
            </h1>
          </FadeIn>
          <FadeIn delay={260}>
            <p className="mt-8 max-w-2xl text-cream/80 text-lg leading-[1.85] font-light">
              100% of donations underwrite retreats for military members, law enforcement,
              first responders, and other quiet professionals.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* DONATION FORM */}
      <section className="container-x py-20 lg:py-28" data-testid="donate-form-section">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-20">
          <FadeIn className="lg:col-span-7">
            <form onSubmit={onDonate} data-testid="donation-form" className="space-y-12">
              <div>
                <p className="overline mb-5">Choose an amount</p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {PRESETS.map((p) => (
                    <button
                      type="button"
                      key={p.id}
                      onClick={() => setTier(p.id)}
                      data-testid={`donate-tier-${p.id}`}
                      className={`text-left p-5 lg:p-6 border transition-all duration-500 ${
                        tier === p.id
                          ? "border-moss bg-moss text-cream"
                          : "border-moss/20 hover:border-moss text-coal"
                      }`}
                    >
                      <div className="font-serif text-3xl font-medium">{p.label}</div>
                      <div className={`mt-1 text-xs tracking-wide ${tier === p.id ? "text-cream/70" : "text-coal/60"}`}>
                        {p.helper}
                      </div>
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => setTier("custom")}
                    data-testid="donate-tier-custom"
                    className={`text-left p-5 lg:p-6 border transition-all duration-500 ${
                      tier === "custom"
                        ? "border-moss bg-moss text-cream"
                        : "border-moss/20 hover:border-moss text-coal"
                    }`}
                  >
                    <div className="font-serif text-3xl font-medium italic">Other</div>
                    <div className={`mt-1 text-xs tracking-wide ${tier === "custom" ? "text-cream/70" : "text-coal/60"}`}>
                      Your choice
                    </div>
                  </button>
                </div>

                {tier === "custom" && (
                  <div className="mt-8">
                    <label className="label-clean">Amount (USD)</label>
                    <div className="flex items-baseline gap-2">
                      <span className="font-serif text-3xl text-clay">$</span>
                      <input
                        type="number"
                        min="5"
                        max="25000"
                        step="1"
                        required
                        value={customAmount}
                        onChange={(e) => setCustomAmount(e.target.value)}
                        placeholder="100"
                        className="input-clean flex-1"
                        data-testid="donate-custom-amount-input"
                      />
                    </div>
                    <p className="mt-3 text-xs text-coal/55">Minimum $5 · Maximum $25,000.</p>
                  </div>
                )}
              </div>

              <div>
                <p className="overline mb-5">About you (optional)</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                  <div>
                    <label className="label-clean">Name</label>
                    <input value={name} onChange={(e) => setName(e.target.value)} className="input-clean" data-testid="donate-name-input" />
                  </div>
                  <div>
                    <label className="label-clean">Email (for receipt)</label>
                    <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="input-clean" data-testid="donate-email-input" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="label-clean">In honor or memory of (optional)</label>
                    <input value={dedication} onChange={(e) => setDedication(e.target.value)} className="input-clean" placeholder="A name, unit, or quiet dedication" data-testid="donate-dedication-input" />
                  </div>
                </div>
              </div>

              <div className="pt-2">
                <button type="submit" disabled={loading} className="btn-primary w-full md:w-auto disabled:opacity-50" data-testid="donate-submit-btn">
                  {loading ? "Preparing secure checkout…" : "Donate securely"}
                </button>
                <p className="mt-4 text-xs text-coal/55 flex items-center gap-2">
                  <Shield size={14} strokeWidth={1.4} className="text-clay" />
                  Secure checkout via Clover.
                </p>
                <p className="mt-2 text-xs text-coal/70 leading-relaxed">
                  <strong>The Shadow Foundation is a registered 501(c)(3) nonprofit organization.</strong> Your donation is
                  tax-deductible to the fullest extent allowed by law. A receipt will be emailed to you for your records.
                </p>
              </div>
            </form>
          </FadeIn>

          {/* Side panel */}
          <aside className="lg:col-span-5 lg:pl-6">
            <FadeIn delay={140}>
              <div className="bg-oat p-10 lg:p-12">
                <HeartHandshake strokeWidth={1.2} size={32} className="text-clay" />
                <h3 className="mt-6 font-serif text-3xl text-moss font-medium leading-tight">
                  Where your gift goes
                </h3>
                <ul className="mt-6 space-y-4 text-coal/75 leading-[1.85]">
                  <li className="flex gap-3"><span className="w-5 h-px bg-clay mt-3 flex-shrink-0" />Underwrites lodging for guests who would not otherwise come</li>
                  <li className="flex gap-3"><span className="w-5 h-px bg-clay mt-3 flex-shrink-0" />Provides meals, supplies, and quiet hospitality during stays</li>
                  <li className="flex gap-3"><span className="w-5 h-px bg-clay mt-3 flex-shrink-0" />Supports New Leaf wellness programming on the land</li>
                  <li className="flex gap-3"><span className="w-5 h-px bg-clay mt-3 flex-shrink-0" />Holds anonymity and dignity at the center of every dollar</li>
                </ul>
              </div>
            </FadeIn>

            <FadeIn delay={260}>
              <div className="mt-8 p-10 lg:p-12 border border-moss/15">
                <p className="overline mb-3">A note</p>
                <p className="font-serif italic text-2xl text-moss/90 leading-[1.4]">
                  "We never charge the people we serve. Your gift is what makes the gate open."
                </p>
              </div>
            </FadeIn>
          </aside>
        </div>
      </section>
    </div>
  );
}
